import React from 'react';
import type { FC } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';

import type { TFormConfirmation } from './models';
import { get } from './helpers/get';
import { prepareScreensMap } from './helpers/prepare-screen-map';
import { splitNamedContainer } from './helpers/split-named-container';

export const FormConfirmation: FC<TFormConfirmation> = ({
    Confirmation,
    alignContent,
    blockSmsRetry,
    children,
    className,
    clearCodeOnError,
    countdownDuration,
    dataTestId,
    hideCountdownSection,
    mobile,
    name,
    onChangeScreen,
    onChangeState,
    onFatalErrorOkButtonClick,
    onInputFinished,
    onSmsRetryClick,
    onTempBlockFinished,
    phone,
    requiredCharAmount,
    tempBlockDuration,
    texts,
}) => {
    const { screenBlocks, textBlocks } = splitNamedContainer(useNamedContainers(children));

    const { values } = useFormState({ subscription: { values: true } });
    const { change } = useForm();

    const getValue = (path?: string) => get({ values, path });
    const getPath = (value: string) => `${name}.${value}`;

    const setConfirmationState = (value: string) => change(getPath('state'), value);
    const setConfirmationScreen = (value: string) => change(getPath('screen'), value);

    const handleInputFinished = (signCode: string) => {
        change(getPath('code'), signCode);
        onInputFinished?.(signCode);
    };

    return (
        <Confirmation
            alignContent={alignContent}
            blockSmsRetry={Boolean(getValue(blockSmsRetry))}
            className={className}
            clearCodeOnError={clearCodeOnError}
            countdownDuration={Number(getValue(countdownDuration))}
            dataTestId={dataTestId}
            getScreensMap={prepareScreensMap(screenBlocks)}
            hideCountdownSection={Boolean(getValue(hideCountdownSection))}
            mobile={mobile}
            onChangeScreen={onChangeScreen || setConfirmationScreen}
            onChangeState={onChangeState || setConfirmationState}
            onFatalErrorOkButtonClick={onFatalErrorOkButtonClick}
            onInputFinished={handleInputFinished}
            onSmsRetryClick={onSmsRetryClick}
            onTempBlockFinished={onTempBlockFinished}
            phone={String(getValue(phone))}
            requiredCharAmount={requiredCharAmount}
            screen={String(getValue(getPath('screen'))) || 'INITIAL'}
            state={String(getValue(getPath('state'))) || 'INITIAL'}
            tempBlockDuration={Number(getValue(tempBlockDuration))}
            texts={{
                ...texts,
                ...textBlocks,
            }}
        />
    );
};
